import { useState, useEffect, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { Bell, Search, Moon, Sun, Menu } from 'lucide-react'
import { Sidebar } from './sidebar'
import { useTheme } from '../../contexts/theme-context'
import { useRealtimeNotifications } from '../../features/dashboard/hooks/use-realtime-notifications'
import { NotificationPanel } from '../../features/notifications'
import { SearchModal } from '../../features/search'
import './layout.css'
import '../../features/notifications/components/notifications.css'

interface MainLayoutProps {
  children?: ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const { resolvedTheme, toggleTheme } = useTheme()
  const { unreadCount } = useRealtimeNotifications()

  // Keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  return (
    <div className="app-layout">
      <a href="#main-content" className="skip-link">Skip to content</a>

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className={`main-wrapper ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <header className="main-header" role="banner">
          <div className="header-left">
            <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle menu">
              <Menu size={24} />
            </button>
          </div>

          <div className="header-right">
            <button
              className="header-icon-btn"
              title="Search (Ctrl+K)"
              onClick={() => setSearchOpen(true)}
            >
              <Search size={20} />
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="header-icon-btn"
                title="Notifications"
                onClick={() => setNotifOpen(!notifOpen)}
              >
                <Bell size={20} />
                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
              </button>
              <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
            </div>
            <button
              className="header-icon-btn"
              onClick={toggleTheme}
              title={resolvedTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {resolvedTheme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
          </div>
        </header>

        <main className="main-content" id="main-content">
          {children ?? <Outlet />}
        </main>

        <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      </div>
    </div>
  )
}
